import { useQuery } from '@tanstack/react-query';
import {
  CircleAlert,
  CopyPlus,
  Download,
  HardDrive,
  Pause,
  Play,
  RotateCcw,
  RotateCw,
  SkipBack,
  SkipForward,
} from 'lucide-react';
import {
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type SyntheticEvent,
} from 'react';

import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ApiError } from '../../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { CopyDialog } from '../evidence/dialogs';
import { skewText } from '../events/api';
import { LivePlayer, type VideoSource } from '../live/LivePlayer';
import {
  deviceRecordingsKey,
  searchDeviceRecordings,
  searchErrorLabels,
  startDeviceHLS,
  startDeviceWebRTC,
  type DeviceRecordings,
} from './deviceApi';
import { spanStartAfter, spanStartBefore, type Span } from './ranges';
import { atWallClock, clock, dayOf } from './time';
import { EventJumps } from './EventJumps';
import { useEventMarks } from './marks';
import { Timeline } from './Timeline';
import { zoomLevels, type ZoomId } from './zoom';

interface Props {
  deviceId: string;
  deviceName: string;
  range: [number, number];
  isToday: boolean;
  timeZone: string | undefined;
}

const serverOnly =
  'Disponível para gravações do servidor: o equipamento só entrega o vídeo em velocidade normal, e as exportações usam o arquivo guardado no Pro Guard. Para guardar um trecho, use “Copiar para o servidor”.';

/**
 * Recordings kept on the device itself (ADR-020): the timeline comes from the
 * device's own search, and playing streams from the device at the chosen
 * moment, at normal speed. Pausing keeps the last frame and closes the stream
 * (the device's uplink is not used while paused); every jump opens a new one.
 */
export function DeviceViewer({ deviceId, deviceName, range, isToday, timeZone }: Props) {
  const { request, can } = useAuth();
  const marks = useEventMarks(deviceId, range, isToday);
  // The range offered by "Copiar para o servidor", taken when it is opened.
  const [copyRange, setCopyRange] = useState<[number, number] | null>(null);
  const [from, to] = range;
  const search = useQuery({
    queryKey: deviceRecordingsKey(deviceId, from, to),
    queryFn: () => searchDeviceRecordings(request, deviceId, from, to),
    retry: false,
  });
  const spans: Span[] = useMemo(
    () =>
      (search.data?.spans ?? [])
        .map((s) => ({ start: Date.parse(s.from), end: Date.parse(s.to) }))
        .filter((s) => s.end > s.start),
    [search.data],
  );
  const stage = useRef<HTMLDivElement>(null);
  const [startAt, setStartAt] = useState<number | null>(null);
  const [offset, setOffset] = useState(0);
  // Paused: the position kept and the last frame shown in place of the video.
  const [pausedAt, setPausedAt] = useState<number | null>(null);
  const [frame, setFrame] = useState<string | null>(null);
  const [zoom, setZoom] = useState<ZoomId>('24h');
  const playhead = startAt !== null ? startAt + offset * 1000 : pausedAt;
  const playing = startAt !== null;

  // The search failed (web port closed), but the device replays from any
  // time over its RTSP port: every moment of the day can be asked for.
  const direct = Boolean(search.data && !search.data.ok && search.data.direct_replay);

  // A moment in a gap plays from the next recorded span.
  const go = (t: number) => {
    const inside = spans.find((s) => t >= s.start && t < s.end);
    const next = direct
      ? Math.min(Math.max(t, from), to - 1000)
      : inside
        ? t
        : spans.find((s) => s.start > t)?.start;
    if (next === undefined) return;
    setOffset(0);
    setPausedAt(null);
    setFrame(null);
    setStartAt(Math.floor(next / 1000) * 1000);
  };

  const pause = () => {
    if (startAt === null) return;
    const video = stage.current?.querySelector('video');
    let still: string | null = null;
    if (video && video.videoWidth > 0) {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext('2d')?.drawImage(video, 0, 0);
        still = canvas.toDataURL('image/jpeg', 0.85);
      } catch {
        still = null; // the frame is a nicety; pausing works without it
      }
    }
    setFrame(still);
    setPausedAt(startAt + offset * 1000);
    setStartAt(null);
  };

  const toggle = () => {
    if (playing) pause();
    else if (pausedAt !== null) go(pausedAt);
    else if (spans[0]) go(spans[0].start);
    else if (direct) go(isToday ? Date.now() - 5 * 60_000 : from);
  };

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const el = e.target;
    if (
      el instanceof HTMLInputElement ||
      el instanceof HTMLSelectElement ||
      el instanceof HTMLTextAreaElement
    )
      return;
    if (e.key === ' ' || e.key === 'k') {
      e.preventDefault();
      toggle();
    } else if ((e.key === 'j' || e.key === 'l') && playhead !== null) {
      go(playhead + (e.key === 'j' ? -10_000 : 10_000));
    }
  };

  const source: VideoSource | undefined = useMemo(
    () =>
      startAt === null
        ? undefined
        : {
            key: String(startAt),
            badge: 'Gravação no equipamento',
            webrtc: (sdp, signal) => startDeviceWebRTC(request, deviceId, startAt, sdp, signal),
            hls: (signal) => startDeviceHLS(request, deviceId, startAt, signal),
          },
    [request, deviceId, startAt],
  );

  if (search.isError) {
    const unsupported = search.error instanceof ApiError && search.error.status === 409;
    return (
      <Panel>
        <EmptyState
          tone={unsupported ? 'neutral' : 'crit'}
          icon={unsupported ? HardDrive : CircleAlert}
          title={
            unsupported
              ? 'Este equipamento não oferece as gravações dele'
              : 'Não foi possível buscar no equipamento'
          }
          description={
            unsupported
              ? 'A busca no equipamento funciona com DVRs, NVRs e câmeras Hikvision/HiLook e com aparelhos ONVIF que gravam (Profile G, ex.: NVRs Intelbras). Use as gravações do servidor.'
              : 'Verifique a conexão com o servidor e tente novamente.'
          }
          {...(unsupported
            ? {}
            : {
                action: (
                  <Button variant="primary" onClick={() => void search.refetch()}>
                    Tentar novamente
                  </Button>
                ),
              })}
        />
      </Panel>
    );
  }

  const last = spans.at(-1);
  const prevSpan = playhead !== null ? spanStartBefore(spans, playhead) : null;
  const nextSpan = playhead !== null ? spanStartAfter(spans, playhead) : null;
  const recorded = spans.reduce((sum, s) => sum + (s.end - s.start), 0);

  return (
    <div className="space-y-3" onKeyDown={onKey}>
      <ClockNotice data={search.data} />
      <div
        ref={stage}
        className="relative aspect-video overflow-hidden rounded-lg border border-line bg-black"
      >
        {source ? (
          <LivePlayer
            deviceId={deviceId}
            profileToken=""
            source={source}
            onProgress={setOffset}
            label={`Gravação no equipamento: ${deviceName}`}
          />
        ) : frame ? (
          <img src={frame} alt={`Pausado: ${deviceName}`} className="size-full object-contain" />
        ) : (
          <div className="grid size-full place-items-center px-6 text-center">
            {search.isPending ? (
              <p role="status" className="text-[13px] text-fg-muted">
                Buscando gravações no equipamento…
              </p>
            ) : direct && pausedAt === null ? (
              <DirectGoTo
                day={dayOf(from, timeZone)}
                timeZone={timeZone}
                isToday={isToday}
                reason={
                  searchErrorLabels[search.data.error_code ?? ''] ??
                  'O equipamento não respondeu à busca.'
                }
                onGo={go}
              />
            ) : spans.length > 0 && pausedAt === null ? (
              <div className="flex flex-col items-center gap-3">
                <p className="text-[13px] text-fg-muted">
                  Escolha um momento na linha do tempo ou comece por aqui:
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      if (spans[0]) go(spans[0].start);
                    }}
                  >
                    Desde o início do dia
                  </Button>
                  <Button
                    onClick={() => {
                      if (last) go(Math.max(last.start, last.end - 5 * 60_000));
                    }}
                  >
                    Mais recente
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        )}
        {playhead !== null && (
          <div className="pointer-events-none absolute top-2 right-2 rounded bg-black/70 px-2 py-0.5 font-mono text-xs text-white">
            ≈ {clock(playhead, timeZone)}
            {!playing && ' · pausado'}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="primary"
          aria-label={playing ? 'Pausar' : 'Reproduzir'}
          disabled={spans.length === 0 && !direct}
          onClick={toggle}
        >
          {playing ? (
            <Pause aria-hidden="true" className="size-4" />
          ) : (
            <Play aria-hidden="true" className="size-4" />
          )}
        </Button>
        <Button
          aria-label="Voltar 10 segundos"
          disabled={playhead === null}
          onClick={() => {
            if (playhead !== null) go(playhead - 10_000);
          }}
        >
          <RotateCcw aria-hidden="true" className="size-4" />
        </Button>
        <Button
          aria-label="Avançar 10 segundos"
          disabled={playhead === null}
          onClick={() => {
            if (playhead !== null) go(playhead + 10_000);
          }}
        >
          <RotateCw aria-hidden="true" className="size-4" />
        </Button>
        <Button
          aria-label="Trecho anterior"
          disabled={prevSpan === null}
          onClick={() => {
            if (prevSpan !== null) go(prevSpan);
          }}
        >
          <SkipBack aria-hidden="true" className="size-4" />
        </Button>
        <Button
          aria-label="Próximo trecho"
          disabled={nextSpan === null}
          onClick={() => {
            if (nextSpan !== null) go(nextSpan);
          }}
        >
          <SkipForward aria-hidden="true" className="size-4" />
        </Button>
        <EventJumps marks={marks} playhead={playhead} dayStart={range[0]} onSeek={go} />
        <label className="flex items-center gap-1.5 text-xs text-fg-muted" title={serverOnly}>
          Velocidade
          <select
            value="1"
            disabled
            className="h-8 rounded-md border border-line-strong bg-canvas px-1.5 text-[13px] text-fg-subtle"
          >
            <option value="1">1×</option>
          </select>
        </label>
        {can('recordings.protect') && (
          <Button
            title="Copia um trecho deste equipamento para o Pro Guard e o protege como evidência"
            onClick={() => {
              setCopyRange(defaultCopyRange(playhead, spans.at(-1) ?? null, range, Date.now()));
            }}
          >
            <CopyPlus aria-hidden="true" className="size-4" />
            Copiar para o servidor
          </Button>
        )}
        <Button disabled title={serverOnly}>
          <Download aria-hidden="true" className="size-4" />
          Exportar
        </Button>
        <div role="group" aria-label="Zoom da linha do tempo" className="ml-auto flex gap-1">
          {zoomLevels.map((z) => (
            <Button
              key={z.id}
              aria-pressed={zoom === z.id}
              className={zoom === z.id ? 'border-accent! text-fg!' : ''}
              onClick={() => {
                setZoom(z.id);
              }}
            >
              {z.label}
            </Button>
          ))}
        </div>
      </div>

      <Timeline
        day={range}
        spans={spans}
        marks={marks}
        playhead={playhead}
        zoom={zoom}
        timeZone={timeZone}
        onSeek={go}
      />
      <p className="text-xs text-fg-muted" aria-live="polite">
        {search.isPending
          ? 'Consultando o equipamento…'
          : !search.data.ok
            ? (searchErrorLabels[search.data.error_code ?? ''] ??
              'O equipamento não respondeu à busca.')
            : spans.length === 0
              ? 'O equipamento não tem gravações neste dia.'
              : `${formatHours(recorded)} gravados no equipamento · ${String(spans.length)} ${
                  spans.length === 1 ? 'trecho' : 'trechos'
                } · atalhos: espaço reproduz/pausa, J/L ±10 s, setas na linha do tempo ±1 min · a posição é aproximada: o horário exato é o da imagem`}
      </p>
      {copyRange && (
        <CopyDialog
          deviceId={deviceId}
          deviceName={deviceName}
          day={dayOf(range[0], timeZone)}
          dayRange={range}
          initial={copyRange}
          timeZone={timeZone}
          onClose={() => {
            setCopyRange(null);
          }}
        />
      )}
    </div>
  );
}

/** Five minutes from the playhead, else the last five recorded, within the day and the past. */
function defaultCopyRange(
  playhead: number | null,
  last: Span | null,
  [dayFrom, dayTo]: [number, number],
  now: number,
): [number, number] {
  const sec = (t: number) => Math.floor(t / 1000) * 1000;
  const end = Math.min(dayTo - 1000, now);
  let a: number;
  if (playhead !== null) a = playhead;
  else if (last) a = Math.min(last.end, end) - 5 * 60_000;
  else a = end - 5 * 60_000;
  a = Math.max(dayFrom, Math.min(a, end - 60_000));
  return [sec(a), sec(Math.min(a + 5 * 60_000, end))];
}

function formatHours(ms: number) {
  const min = Math.round(ms / 60_000);
  return min < 60
    ? `${String(min)} min`
    : `${String(Math.floor(min / 60))} h ${String(min % 60)} min`;
}

function ClockNotice({ data }: { data: DeviceRecordings | undefined }) {
  const c = data?.clock;
  if (!c || c.state === 'ok') return null;
  return (
    <div
      role="status"
      className="flex items-start gap-2 rounded-md border border-warn/30 bg-warn-soft px-3 py-2 text-[13px] text-fg"
    >
      <CircleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-warn" />
      <span>
        {c.state === 'invalid'
          ? 'O relógio deste equipamento está zerado: as gravações feitas desde então estão com data errada e não aparecem na busca por dia. Ative o NTP no equipamento.'
          : `O relógio deste equipamento está ${skewText(c.skew_seconds)}: os horários das gravações podem não coincidir com os do servidor.`}
      </span>
    </div>
  );
}

function Panel({ children }: { children: ReactNode }) {
  return <div className="rounded-lg border border-line bg-surface">{children}</div>;
}

/**
 * Replay from a chosen time when the device did not answer the search but
 * plays any moment over its RTSP port (ADR-041).
 */
function DirectGoTo({
  day,
  timeZone,
  isToday,
  reason,
  onGo,
}: {
  day: string;
  timeZone: string | undefined;
  isToday: boolean;
  reason: string;
  onGo: (t: number) => void;
}) {
  const [time, setTime] = useState('');
  const [error, setError] = useState<string | null>(null);
  const submit = (e: SyntheticEvent) => {
    e.preventDefault();
    const at = atWallClock(day, time, timeZone);
    if (at === null) {
      setError('Informe a hora.');
      return;
    }
    if (at > Date.now()) {
      setError('Escolha um horário no passado.');
      return;
    }
    setError(null);
    onGo(at);
  };
  return (
    <form
      onSubmit={submit}
      className="flex max-w-md flex-col items-center gap-3"
      aria-label="Ir para um horário"
    >
      <p className="text-[13px] text-fg-muted">
        {reason} Mesmo assim, este aparelho toca a partir de um horário pela porta de vídeo: escolha
        a hora.
      </p>
      <div className="flex items-center gap-2">
        <label className="sr-only" htmlFor="direct-time">
          Hora no equipamento
        </label>
        <input
          id="direct-time"
          type="time"
          step={1}
          value={time}
          onChange={(e) => {
            setTime(e.target.value);
          }}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? 'direct-time-error' : undefined}
          className="h-8 rounded-md border border-line-strong bg-canvas px-2 font-mono text-[13px] text-fg aria-invalid:border-crit"
        />
        <Button type="submit" variant="primary">
          Ir
        </Button>
        {isToday && (
          <Button
            onClick={() => {
              onGo(Date.now() - 5 * 60_000);
            }}
          >
            Últimos 5 minutos
          </Button>
        )}
      </div>
      {error && (
        <p id="direct-time-error" role="alert" className="text-xs text-crit">
          {error}
        </p>
      )}
      <p className="text-2xs text-fg-subtle">
        Sem a busca, a linha do tempo não mostra o que foi gravado; clicar nela também toca daquele
        horário.
      </p>
    </form>
  );
}
